import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  Divider,
  Paper,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslate } from 'react-admin';
import { useAppLocale } from '../../hooks/useAppLocale';
import { API_URL } from '../../httpClient';

interface MessageEditHistoryEntry {
  id: number;
  messageId: number;
  previousBody?: string | null;
  editedAt: string;
  createdAt: string;
  metadata?: Record<string, unknown> | null;
}

interface MessageEditHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  messageId: number;
  currentBody?: string | null;
}

export function MessageEditHistoryDialog({
  open,
  onClose,
  messageId,
  currentBody,
}: MessageEditHistoryDialogProps) {
  const translate = useTranslate();
  const appLocale = useAppLocale();
  const [history, setHistory] = useState<MessageEditHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !messageId) return;

    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        const tenantUuid = localStorage.getItem('tenantUuid');

        if (!token || !tenantUuid) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_URL}/message/${messageId}/edit-history`, {
          headers: {
            'x-tenant-user-auth': token,
            'x-tenant-uuid': tenantUuid,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch edit history: ${response.statusText}`);
        }

        const result = await response.json();
        setHistory(result.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [open, messageId]);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(appLocale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
      time: date.toLocaleTimeString(appLocale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    };
  };

  // Build full version history: all previous versions + current version
  const versions = [
    ...history.map((entry, index) => ({
      body: entry.previousBody || '',
      editedAt: entry.editedAt,
      label: translate('resources.message.editHistory.version', {
        smart_count: index + 1,
      }),
      isCurrent: false,
      metadata: entry.metadata,
    })),
    {
      body: currentBody || '',
      editedAt: new Date().toISOString(),
      label: translate('resources.message.editHistory.currentVersion'),
      isCurrent: true,
      metadata: null,
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        {translate('resources.message.editHistory.title')}
        <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box py={2}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {!isLoading && !error && history.length === 0 && (
          <Box py={2}>
            <Typography color="textSecondary" variant="body2">
              {translate('resources.message.editHistory.empty')}
            </Typography>
          </Box>
        )}

        {!isLoading && !error && history.length > 0 && (
          <List dense sx={{ py: 0 }}>
            {versions.map((version, index) => {
              const { date, time } = formatDateTime(version.editedAt);
              const isLast = index === versions.length - 1;
              const providerInfo = version.metadata?.source ? ` (${version.metadata.source})` : '';

              return (
                <Box key={index}>
                  <ListItem
                    sx={{
                      py: 2,
                      px: 0,
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      position: 'relative',
                      '&::before': !isLast ? {
                        content: '""',
                        position: 'absolute',
                        left: 18,
                        top: 60,
                        bottom: -16,
                        width: 2,
                        bgcolor: 'divider',
                      } : undefined,
                    }}
                  >
                    {/* Version header with icon and label */}
                    <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: version.isCurrent ? 'primary.main' : 'transparent',
                          border: version.isCurrent ? 'none' : '2px solid',
                          borderColor: 'primary.main',
                          color: version.isCurrent ? 'white' : 'primary.main',
                          flexShrink: 0,
                          '& svg': {
                            fontSize: 20,
                          },
                        }}
                      >
                        <EditIcon />
                      </Box>
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" fontWeight={version.isCurrent ? 600 : 500}>
                            {version.label}
                          </Typography>
                          {version.isCurrent && (
                            <Chip
                              label={translate('resources.message.editHistory.current')}
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {date} • {time}{providerInfo}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Message content */}
                    <Paper
                      variant="outlined"
                      sx={{
                        ml: 5.5,
                        p: 1.5,
                        bgcolor: version.isCurrent ? 'action.hover' : 'transparent',
                        borderColor: version.isCurrent ? 'primary.main' : 'divider',
                        borderWidth: version.isCurrent ? 2 : 1,
                      }}
                    >
                      {version.body ? (
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: 'text.primary',
                          }}
                        >
                          {version.body}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            fontStyle: 'italic',
                            color: 'text.secondary',
                          }}
                        >
                          {translate('resources.message.editHistory.emptyContent')}
                        </Typography>
                      )}
                    </Paper>
                  </ListItem>
                  {!isLast && <Divider variant="inset" component="li" sx={{ ml: 5.5 }} />}
                </Box>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
